import { Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { assetsConst } from 'src/app/core/const/assets.const';
import { responseStatus } from 'src/app/core/const/responseStatus.const';
import {
  geoLongLatFromPolygonPath,
  MAP_POLYGON_STYLE,
  polygonPathsFromGeoLongLat
} from 'src/app/core/helpers/mapPolygon.helper';
import { manageAreaService } from 'src/app/core/services/manageArea.service';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { ToasterService } from 'src/app/core/services/toastr.service';
import { CommonResponse } from 'src/app/models/commomResponse';
declare let google: any;

@Component({
  selector: 'megastore-admin-addarea',
  templateUrl: './addarea.component.html',
  styleUrls: ['./addarea.component.scss']
})
export class AddareaComponent implements OnDestroy {
  protected assetsConst = assetsConst;
  protected addAreaForm: FormGroup;
  protected areaId: string | null = null;
  submitted = false;

  destroy$: Subject<boolean> = new Subject<boolean>();
  map: any;
  geoLongLat: any = [];
  polygon: any;
  previewPolyline: any;
  draftPoints: any[] = [];
  draftMarkers: any[] = [];
  mapClickListener: any;
  pathListeners: any[] = [];
  draggable = true;
  lat: number | undefined = 0;
  lng: number | undefined = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { data: string },
    private dialog: MatDialogRef<AddareaComponent>,
    private formBuilder: FormBuilder,
    private _spinnerService: SpinnerService,
    private _toasterService: ToasterService,
    private _translateService: TranslateService,
    private _areaService: manageAreaService
  ) {
    this.addAreaForm = this.formBuilder.group({
      name: [
        '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(40)]
      ]
    });
    if (this.data.data !== undefined) {
      this.areaId = this.data.data;
      this.getAreaDetails();
    }
    this.getCurrentLocation();
  }

  get canCompletePolygon(): boolean {
    return this.draftPoints.length >= 3 && !this.polygon;
  }

  getCurrentLocation(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        this.lat = position.coords.latitude;
        this.lng = position.coords.longitude;
        setTimeout(() => {
          this.initMap();
          if (this.areaId) this.getAreaDetails();
        }, 1000);
      });
    }
  }

  initMap(): void {
    this.cleanupDrawingState();
    this.map = new google.maps.Map(
      document.getElementById('map') as HTMLElement,
      {
        zoom: 12,
        center: {
          lat: this.lat,
          lng: this.lng
        },
        disableDefaultUI: true,
        mapTypeControl: false,
        scaleControl: true,
        zoomControl: true,
        zoomControlOptions: {
          style: google.maps.ZoomControlStyle.SMALL
        }
      }
    );
    this.startDrawingMode();
  }

  startDrawingMode(): void {
    this.stopDrawingMode();
    this.mapClickListener = this.map.addListener('click', (event: any) => {
      if (this.polygon) {
        return;
      }
      this.addDraftPoint(event.latLng);
    });
  }

  addDraftPoint(latLng: any): void {
    this.draftPoints.push(latLng);
    this.draftMarkers.push(
      new google.maps.Marker({
        position: latLng,
        map: this.map,
        clickable: false
      })
    );
    this.updatePreviewPolyline();
  }

  updatePreviewPolyline(): void {
    if (this.previewPolyline) {
      this.previewPolyline.setMap(null);
      this.previewPolyline = null;
    }

    if (this.draftPoints.length < 2) {
      return;
    }

    this.previewPolyline = new google.maps.Polyline({
      path: this.draftPoints,
      strokeColor: MAP_POLYGON_STYLE.strokeColor,
      strokeOpacity: MAP_POLYGON_STYLE.strokeOpacity,
      strokeWeight: MAP_POLYGON_STYLE.strokeWeight,
      map: this.map
    });
  }

  completePolygon(): void {
    if (this.draftPoints.length < 3) {
      this._toasterService.errToastr('Add at least 3 points to create a polygon');
      return;
    }

    this.stopDrawingMode();
    this.clearDraftGraphics();

    this.polygon = new google.maps.Polygon({
      paths: [...this.draftPoints],
      ...MAP_POLYGON_STYLE,
      draggable: this.draggable,
      editable: this.draggable
    });
    this.polygon.setMap(this.map);
    this.draftPoints = [];
    this.syncGeoLongLatFromPolygon(this.polygon);
    this.attachPolygonChangeListeners(this.polygon);
  }

  syncGeoLongLatFromPolygon(polygon: any): void {
    this.geoLongLat = geoLongLatFromPolygonPath(polygon.getPath());
  }

  attachPolygonChangeListeners(polygon: any): void {
    this.clearPathListeners();
    const path = polygon.getPath();
    const updateCoordinates = () => this.syncGeoLongLatFromPolygon(polygon);

    this.pathListeners = [
      path.addListener('set_at', updateCoordinates),
      path.addListener('insert_at', updateCoordinates),
      path.addListener('remove_at', updateCoordinates),
      google.maps.event.addListener(polygon, 'dragend', updateCoordinates)
    ];
  }

  deletePolygon(): void {
    this.geoLongLat = [];
    this.draggable = true;
    this.cleanupDrawingState();
    this.initMap();
  }

  private getAreaDetails(): void {
    this._spinnerService.addToLoader('getArea');
    this._areaService.getAreaById(this.areaId).subscribe({
      next: (res: any) => {
        this._spinnerService.removeFromLoader('getArea');
        this.addAreaForm.patchValue(res);
      },
      error: () => {
        this._spinnerService.removeFromLoader('getArea');
      }
    });
  }

  setPrePolygon(): void {
    this.cleanupDrawingState();
    this.map = new google.maps.Map(
      document.getElementById('map') as HTMLElement,
      {
        zoom: 12,
        center: {
          lat: this.lat,
          lng: this.lng
        },
        disableDefaultUI: true,
        mapTypeControl: false,
        scaleControl: true,
        zoomControl: true,
        zoomControlOptions: {
          style: google.maps.ZoomControlStyle.SMALL
        }
      }
    );

    this.polygon = new google.maps.Polygon({
      paths: polygonPathsFromGeoLongLat(this.geoLongLat),
      ...MAP_POLYGON_STYLE,
      draggable: this.draggable,
      editable: false
    });
    this.polygon.setMap(this.map);
  }

  onSubmit(): void {
    this.submitted = true;
    this.addAreaForm.get('geofenceId')?.enable();
    this.addAreaForm.patchValue({
      name: this.addAreaForm.get('name')?.value.trim()
    });
    if (this.addAreaForm.valid) {
      this.onFormValid();
    }
  }
  submit(): void {
    this.submitted = true;
    this._spinnerService.addToLoader('addGeofence');
    if (this.addAreaForm.valid && this.geoLongLat) {
      const param = {
        name: this.addAreaForm.value.name.trim(),
        geoFence: {
          type: 'Polygon',
          coordinates: [this.geoLongLat]
        }
      };
      if (!this.areaId) {
        if (param.geoFence.coordinates[0].length === 0) {
          this._toasterService.errToastr('Select Geofence');
          return;
        }

        this._areaService.addArea(param).subscribe(
          (res: CommonResponse) => {
            this._spinnerService.removeFromLoader('addGeofence');
            if (res.statusCode === responseStatus.SUCCESS) {
              history.back();
              this.addAreaForm.reset();
              this._toasterService.successToastr(
                this._translateService.instant('Geo-FenceAddedSuccefully.')
              );
              this.submitted = false;
            }
          },
          (error: () => void) => {
            this._spinnerService.removeFromLoader('addGeofence');
          }
        );
      } else {
        if (param.geoFence.coordinates[0].length === 0) {
          param.geoFence.coordinates[0] = this.geoLongLat;
        }
        if (!this.geoLongLat && !param.geoFence.coordinates[0]) {
          this._toasterService.errToastr('Select Geofence');
          return;
        }

        this._areaService.updateAreaManager(param, this.areaId).subscribe(
          (res: CommonResponse) => {
            if (res.statusCode === responseStatus.SUCCESS) {
              this._spinnerService.removeFromLoader('addGeofence');
              history.back();
              this.addAreaForm.reset();
              this.submitted = false;
              this._toasterService.successToastr(
                this._translateService.instant('Geo-FenceUpdatedSuccefully.')
              );
            } else {
              this._spinnerService.removeFromLoader('addGeofence');
            }
          },
          (error: () => void) => {
            this._spinnerService.removeFromLoader('addGeofence');
          }
        );
      }
    } else {
      this.addAreaForm.markAllAsTouched();
      this._spinnerService.removeFromLoader('addGeofence');
    }
  }

  onFormValid(): void {
    this._spinnerService.addToLoader('addTeam');
    (this.data.data ? this._areaService.updateAreaManager(this.addAreaForm.value, this.areaId) : this._areaService.addArea(this.addAreaForm.value)
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: CommonResponse) => {
          if (res) {
            this._spinnerService.removeFromLoader('addTeam');
            this._toasterService.successToastr(
              this._translateService.instant('Successful')
            );
            this.dialog.close(res);
          }
        },
        error: () => {
          this._spinnerService.removeFromLoader('addTeam');
        }
      });
  }

  stopDrawingMode(): void {
    if (this.mapClickListener) {
      google.maps.event.removeListener(this.mapClickListener);
      this.mapClickListener = null;
    }
  }

  clearDraftGraphics(): void {
    this.draftMarkers.forEach(marker => marker.setMap(null));
    this.draftMarkers = [];
    this.draftPoints = [];

    if (this.previewPolyline) {
      this.previewPolyline.setMap(null);
      this.previewPolyline = null;
    }
  }

  clearPathListeners(): void {
    this.pathListeners.forEach(listener => google.maps.event.removeListener(listener));
    this.pathListeners = [];
  }

  cleanupDrawingState(): void {
    this.stopDrawingMode();
    this.clearPathListeners();
    this.clearDraftGraphics();

    if (this.polygon) {
      this.polygon.setMap(null);
      this.polygon = null;
    }
  }

  ngOnDestroy(): void {
    this.cleanupDrawingState();
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }
}
